import React, { useEffect, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import client from "@/lib/api";
import { Check, X } from "lucide-react";

/**
 * Auto-detecting WhatsApp phone input.
 * - 8 digits (Oman) → +968 assumed
 * - + prefix → country auto-detected
 * - Debounced server validation via POST /api/utils/phone/parse
 * - Emits { raw, parsed } via onChange so the parent can send e164 on submit.
 *
 * Stability contract (critical — see typing bug reported 2026-02):
 *   The input is **fully self-controlled via local `raw` state**. The parent's
 *   `value` prop is only consumed when the parent EXTERNALLY resets it (e.g.
 *   after submit). This guarantees:
 *     - Every keystroke stays exactly as typed — no re-normalization on change.
 *     - Cursor never jumps because the DOM value is only ever the user's input.
 *     - Async validation results NEVER stomp the raw value: the async callback
 *       compares against `rawRef.current` and drops stale responses.
 *   The "Invalid" indicator is also gated by `!focused` so partial numbers do
 *   not flash red while the user is still typing.
 */
export function WhatsappInput({ value, onChange, testid = "customer-whatsapp-input" }) {
  const [raw, setRaw] = useState(value || "");
  const [status, setStatus] = useState({ state: "idle" }); // idle | checking | valid | invalid
  const [focused, setFocused] = useState(false);
  const rawRef = useRef(raw);
  rawRef.current = raw;
  const timer = useRef(null);

  // Sync only on EXTERNAL parent resets (rawRef.current is always the current
  // typed value; when the parent clears/replaces `value` from outside, we adopt
  // it). Prevents the parent → child feedback loop that caused digits to vanish.
  useEffect(() => {
    if ((value || "") !== rawRef.current) {
      setRaw(value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const validate = (v) => {
    if (timer.current) clearTimeout(timer.current);
    if (!v || !v.trim()) {
      setStatus({ state: "idle" });
      return;
    }
    // Preserve an existing "valid" state until we know otherwise — avoids the
    // green check briefly disappearing mid-keystroke on stable inputs.
    setStatus((s) => (s.state === "valid" ? s : { state: "checking" }));
    timer.current = setTimeout(async () => {
      const captured = v;
      try {
        const { data } = await client.post("/utils/phone/parse", { raw: captured });
        // Stale-response guard: the user has typed more since we scheduled.
        // Drop this result entirely — a fresher validate() is already in-flight.
        if (rawRef.current !== captured) return;
        if (data.valid) {
          setStatus({ state: "valid", ...data });
          onChange({ raw: rawRef.current, parsed: data });
        } else {
          setStatus({ state: "invalid", error: data.error || "Invalid number" });
          onChange({ raw: rawRef.current, parsed: null });
        }
      } catch {
        if (rawRef.current !== captured) return;
        setStatus({ state: "invalid", error: "Could not validate" });
        onChange({ raw: rawRef.current, parsed: null });
      }
    }, 500);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setRaw(v);
    onChange({ raw: v, parsed: null }); // parent sees the latest raw immediately
    validate(v);
  };

  return (
    <div>
      <Label className="overline">Customer WhatsApp Number</Label>
      <div className="relative mt-2">
        <Input
          value={raw}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          required
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="92259800  or  +971 50 123 4567"
          data-testid={testid}
          className="bg-black border-white/20 text-white h-10 pr-9 font-mono"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {status.state === "valid" && (
            <Check size={16} style={{ color: "var(--success)" }} data-testid="whatsapp-valid-icon" />
          )}
          {status.state === "invalid" && !focused && (
            <X size={16} style={{ color: "var(--live)" }} data-testid="whatsapp-invalid-icon" />
          )}
          {status.state === "checking" && (
            <span className="w-2 h-2 rounded-full bg-white/40 inline-block animate-pulse" />
          )}
        </div>
      </div>
      <div className="mt-1.5 min-h-[18px] text-xs">
        {status.state === "valid" && (
          <span className="text-white/70" data-testid="whatsapp-country-hint">
            <span className="mr-1.5">{status.flag}</span>
            {status.country_name}
            <span className="ml-2 text-white/40 font-mono">{status.e164}</span>
          </span>
        )}
        {status.state === "invalid" && !focused && (
          <span style={{ color: "var(--live)" }} data-testid="whatsapp-error">
            {status.error}
          </span>
        )}
      </div>
    </div>
  );
}
